import { Asset } from "./Asset";
import { AssetRealEstate } from "./AssetDetail";
import { AssetCategory } from "./AssetCategory";
import { Country, State, County, CountyType } from "./Geography";
import { AssetSubCategory } from "./AssetSubCategory";

export enum PlaceTypes {
  Primary = "Primary",
  Secondary = "Secondary",
  Business = "Business",
}

export type PlaceJSON = {
  id?: number;
  type?: string;
  formatted_address?: string;
  place_id?: string;
  number?: string;
  street?: string;
  address2?: string;
  city?: string;
  state?: string;
  county?: string;
  postal_code?: string;
  country?: string;
};

export class Place {
  constructor(
    public id?: number,
    public type?: PlaceTypes,
    public formattedAddress?: string,
    public placeId?: string,
    public number?: string,
    public street?: string,
    public address2?: string,
    public city?: string,
    public state?: string,
    public county?: string,
    public postal_code?: string,
    public country?: string
  ) {}

  static fromJSON = (json: PlaceJSON): Place => {
    return new Place(
      json.id,
      json.type as PlaceTypes,
      json.formatted_address,
      json.place_id,
      json.number,
      json.street,
      json.address2,
      json.city,
      json.state,
      json.county,
      json.postal_code,
      json.country
    );
  };

  toJSON(): any {
    return {
      id: this.id,
      type: this.type?.toString(),
      formatted_address: this.formattedAddress,
      place_id: this.placeId,
      number: this.number,
      street: this.street,
      address2: this.address2,
      city: this.city,
      state: this.state,
      county: this.county,
      postal_code: this.postal_code,
      country: this.country,
    };
  }

  stripCountyName(
    fullCountyName?: string,
    countyTypes?: CountyType[]
  ): string | undefined {
    let county = fullCountyName;
    countyTypes?.forEach((countyType: CountyType) => {
      if (countyType?.type) {
        county = county?.replace(countyType.type, "");
      }
    });
    return county?.trim();
  }

  toRealEstateAsset(
    estateId?: number,
    states?: State[],
    counties?: County[],
    countyTypes?: CountyType[],
    countries?: Country[],
    categories?: AssetCategory[]
  ): Asset {
    const address1 = `${this.number || ""} ${this.street}`.trim();
    const asset = new Asset(this.id, address1);
    asset.detail = new AssetRealEstate(
      address1,
      this.address2,
      undefined, // will be set below
      this.city,
      undefined, // will be set below
      this.postal_code,
      undefined, // will be set below
      this.placeId,
      this.formattedAddress
    );
    asset.estateId = estateId;
    (asset.detail as AssetRealEstate).states_id = states?.find(
      (state: State) => state.code === this.state
    )?.id;
    (asset.detail as AssetRealEstate).countries_id = countries?.find(
      (country: Country) => country.code?.substr(0, 2) === this.country
    )?.id;
    (asset.detail as AssetRealEstate).counties_id = counties?.find(
      (county: County) =>
        county.name === this.stripCountyName(this.county, countyTypes)
    )?.id;
    asset.category = categories?.filter(
      (cat: AssetCategory) => cat.category === "Real Estate"
    )[0];
    asset.subcategory = asset.category?.sub_categories?.filter(
      (subCat: AssetSubCategory) => subCat.subcategory === "Primary Home"
    )[0];
    return asset;
  }
}
